import { useAutoSave } from "@/dentlab/src/hooks/useAutosave/useAutosave";
import { useRef } from "react";

interface FormSectionWithAutosaveProps<T> {
	onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
	children: React.ReactNode;
	formData: T;
	isPendingChanges: boolean;
	formErrors?: Record<string, string | null>;
	className?: string;
}

export const FormSectionWithAutosave = <T extends object>({
	onSubmit,
	children,
	formData,
	isPendingChanges,
	formErrors,
	className,
}: FormSectionWithAutosaveProps<T>) => {
	const isFormValid = () => {
		return Object.values(formErrors || {}).every((error) => error === null);
	};
	const formRef = useRef<HTMLFormElement>(null);
	useAutoSave({
		data: formData,
		onSave: () => {
			if (isFormValid() && formRef.current) {
				if (formRef.current.checkValidity()) {
					const event = new Event(
						"submit"
					) as unknown as React.FormEvent<HTMLFormElement>;
					handleSubmit(event);
				} else {
					formRef.current.reportValidity();
				}
			}
		},
		shouldSave: isPendingChanges && isFormValid(),
	});

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (formRef.current?.checkValidity()) {
			onSubmit(event);
		}
	};

	return (
		<form
			ref={formRef}
			onSubmit={handleSubmit}
			noValidate
			className={className}
		>
			{children}
		</form>
	);
};
